"use client";

import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherX } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  getListOrganisationsQueryControllerListQueryKey,
  useCreateOrganisationCommandControllerCreate,
} from "@/generated/organisations/organisations";

const organisationSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Nursery group name is required")
    .max(100, "Name must be less than 100 characters"),
  postcode: z
    .string()
    .trim()
    .min(1, "Postcode is required")
    .max(20, "Postcode must be less than 20 characters"),
  country: z
    .string()
    .trim()
    .min(1, "Country is required")
    .max(100, "Country must be less than 100 characters"),
  notes: z
    .string()
    .trim()
    .max(500, "Notes must be less than 500 characters")
    .optional(),
});

interface CreateOrganisationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateOrganisationModal({
  open,
  onOpenChange,
}: CreateOrganisationModalProps) {
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [postcode, setPostcode] = useState("");
  const [country, setCountry] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { mutate: createOrganisation, isPending } =
    useCreateOrganisationCommandControllerCreate({
      mutation: {
        onSuccess: async () => {
          toast.success("Nursery group created successfully");
          const queryKey = getListOrganisationsQueryControllerListQueryKey({});
          await queryClient.invalidateQueries({ queryKey });
          await queryClient.refetchQueries({ queryKey });
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to create nursery group";
          toast.error(errorMessage);
        },
      },
    });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setName("");
      setPostcode("");
      setCountry("");
      setNotes("");
      setErrors({});
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const result = organisationSchema.safeParse({
      name,
      postcode,
      country,
      notes: notes || undefined,
    });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((err) => {
        const path = err.path.join(".");
        fieldErrors[path] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    createOrganisation({
      data: {
        name,
        postcode,
        country,
        notes: notes || undefined,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-2xl max-h-[90vh] overflow-y-auto p-0"
      >
        <DialogTitle className="sr-only">Create Nursery Group</DialogTitle>
        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Create Nursery Group
              </span>
              <span className="text-body font-body text-subtext-color">
                Add a new nursery group to the platform.
              </span>
            </div>
            <IconButton
              variant="neutral-tertiary"
              size="medium"
              icon={<FeatherX />}
              onClick={() => onOpenChange(false)}
            />
          </div>

          {/* Form Content */}
          <div className="flex flex-col gap-6 px-6 py-6">
            {/* Organisation Details */}
            <div className="flex flex-col gap-4">
              <TextField
                className="h-auto w-full"
                size="large"
                label="Nursery group name"
                error={!!errors.name}
                helpText={errors.name}
              >
                <TextField.Input
                  placeholder="e.g., Acme Corporation"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isPending}
                />
              </TextField>

              <div className="flex w-full items-start gap-4">
                <TextField
                  className="h-auto grow shrink-0 basis-0"
                  size="large"
                  label="Postcode"
                  error={!!errors.postcode}
                  helpText={errors.postcode}
                >
                  <TextField.Input
                    placeholder="e.g., SW1A 1AA"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    disabled={isPending}
                  />
                </TextField>

                <TextField
                  className="h-auto grow shrink-0 basis-0"
                  size="large"
                  label="Country"
                  error={!!errors.country}
                  helpText={errors.country}
                >
                  <TextField.Input
                    placeholder="e.g., United Kingdom"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    disabled={isPending}
                  />
                </TextField>
              </div>

              <TextArea
                className="h-auto w-full"
                label="Notes (optional)"
                error={!!errors.notes}
                helpText={errors.notes}
              >
                <TextArea.Input
                  placeholder="Additional notes about this nursery group..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={isPending}
                />
              </TextArea>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-solid border-secondary-beige-200 px-6 py-4">
            <Button
              type="button"
              variant="neutral-primary"
              size="large"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              size="large"
              disabled={isPending}
            >
              {isPending ? "Creating..." : "Create Nursery Group"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
